import Chart, { ChartTypeRegistry, ChartData } from "chart.js/auto";
import type { Chart as ChartType, ChartConfiguration } from "chart.js/auto";
import ChartDataLabels from "chartjs-plugin-datalabels";
import chartsDefaultOptions from "./chartsDefaultOptions";
import defaultDarkOptions from "./chartsDefaultDarkmodeOptions";
import merge, { Options } from "deepmerge";
import { onUnmounted, ref, watch } from "vue";

type DefaultOptions = typeof chartsDefaultOptions;
type ChartOptions = ChartConfiguration | {};
interface DefaultOptionsEnchanced extends DefaultOptions {
  [key: string]: any;
}
interface DeepmergeOptions extends Options {
  cloneUnlessOtherwiseSpecified: (item: object, options: object) => object;
}

interface DarkModeProps {
  darkTicksColor: string;
  darkLabelColor: string;
  darkGridLinesColor: string;
  darkBgColor: string;
  darkBgColorLight: string;
}

function generateCharts(
  canvasRef: HTMLCanvasElement | HTMLElement,
  type: keyof ChartTypeRegistry,
  data: ChartData = { datasets: [] },
  options: ChartOptions = {},
  darkOptions: ChartOptions = {},
  darkProps: DarkModeProps,
  colorMode: "light" | "dark"
) {
  const _data = ref(data);
  const _chart = ref<ChartType | null>(null);
  const _options = ref<ChartOptions | null>(null);
  const _plugins = ref<any[] | null>(null);
  const _darkOptions = ref<any>(null);

  const _mode = ref(colorMode || "light");
  const _isAnimating = ref(false);
  const _timeout = ref<any>(null);

  setupCharts();

  function setupCharts() {
    _options.value = mergeOptions(options, type, chartsDefaultOptions);
    _darkOptions.value = getDarkConfig(darkOptions, darkProps);
  }

  function mergeOptions(
    options: ChartOptions,
    type: string,
    defaultOptions: DefaultOptionsEnchanced
  ) {
    if (!options) {
      options = {};
    }

    const mergeObjects = (
      target: ChartOptions[],
      source: ChartOptions[],
      options: DeepmergeOptions
    ) => {
      const destination = target.slice();
      source.forEach((item, index) => {
        const sourceItem = item as Object;
        if (typeof destination[index] === "undefined") {
          destination[index] = options.cloneUnlessOtherwiseSpecified(
            sourceItem,
            options
          );
        } else if (options.isMergeableObject?.(sourceItem)) {
          const targetOptions = target[index] || {};

          destination[index] = merge(targetOptions, sourceItem, options);
        } else if (target.indexOf(item) === -1) {
          destination.push(item);
        }
      });
      return destination;
    };

    return merge(defaultOptions[type], options, {
      arrayMerge: mergeObjects,
    });
  }

  function addDataLabels() {
    _plugins.value = [ChartDataLabels];
  }

  function getOptionsByMode() {
    return _mode.value === "dark" ? _darkOptions.value.options : _options.value;
  }

  function renderChart(disableAnimation = false) {
    destroyChart();

    if (canvasRef instanceof HTMLCanvasElement) {
      const options = {
        type,
        data: _data.value,
        options: getOptionsByMode(),
        plugins: _plugins.value,
      } as ChartConfiguration;

      const context = canvasRef.getContext("2d");

      if (
        disableAnimation &&
        options.options &&
        typeof options.options === "object"
      ) {
        (options.options as any).animation = false;
      }

      changeDatasetBorderColor(); // changes borderColor for pie, doughnut, polarArea charts in dark mode

      if (context) {
        const chart = new Chart(context, options) as any;
        _chart.value = chart;

        if (!disableAnimation && chart.options?.animation?.duration > 0) {
          _isAnimating.value = true;
          _timeout.value = setTimeout(() => {
            _isAnimating.value = false;
          }, chart.options.animation.duration);
        }
      }
    }
  }

  function updateChart(data: ChartData, options: ChartOptions) {
    _options.value = mergeOptions(options, type, chartsDefaultOptions);
    _data.value = data;

    renderChart(true);
  }

  function destroyChart() {
    if (_chart.value) {
      _chart.value.destroy();
      _chart.value = null;
    }
  }

  function updateDarkmodeOptions(newDarkOptions: object) {
    _darkOptions.value = getDarkConfig(newDarkOptions, darkProps);
  }

  function createXYScale(dataAttributes: DarkModeProps) {
    return {
      y: {
        ticks: { color: dataAttributes.darkTicksColor },
        grid: { color: dataAttributes.darkGridLinesColor },
        title: { color: dataAttributes.darkLabelColor },
      },
      x: {
        ticks: { color: dataAttributes.darkTicksColor },
        grid: { color: dataAttributes.darkGridLinesColor },
        title: { color: dataAttributes.darkLabelColor },
      },
    };
  }

  function createRScale(dataAttributes: DarkModeProps) {
    return {
      r: {
        ticks: {
          color: dataAttributes.darkTicksColor,
          backdropColor: dataAttributes.darkBgColor,
        },
        grid: { color: dataAttributes.darkGridLinesColor },
        pointLabels: { color: dataAttributes.darkTicksColor },
        title: { color: dataAttributes.darkLabelColor },
      },
    };
  }

  function getDarkConfig(config: ChartOptions, propsConfig: any) {
    const dataAttributes = {
      ...defaultDarkOptions,
      ...propsConfig,
    };

    const xyScale = createXYScale(dataAttributes);
    const rScale = createRScale(dataAttributes);

    const radials = ["pie", "doughnut", "polarArea", "radar"];

    let scales = {};

    if (!radials.includes(type)) {
      scales = xyScale;
    } else if (["polarArea", "radar"].includes(type)) {
      scales = rScale;
    }

    const combinedOptions = {
      scales,
      plugins: {
        datalabels: {
          color: dataAttributes.darkLabelColor,
        },
        legend: {
          labels: {
            color: dataAttributes.darkLabelColor,
          },
        },
        title: {
          color: dataAttributes.darkLabelColor,
        },
      },
      ...config,
    };

    // Merge for any options that are not included in defaultDarkOptions. E.g. those from plugins - formatter, tooltip, etc.
    const optionsBeforeFinalMerging = merge(
      _options.value || {},
      combinedOptions
    );

    // Final merge with default options to ensure all necessary default options are included
    const options = mergeOptions(
      optionsBeforeFinalMerging,
      type,
      chartsDefaultOptions
    );

    // combine config
    config = {
      ...dataAttributes,
      options: options,
    };

    return config;
  }

  function changeDatasetBorderColor() {
    if (!_data.value || !_data.value.datasets) {
      return;
    }

    [..._data.value.datasets].forEach(
      (set) =>
        ["pie", "doughnut", "polarArea"].includes(type) &&
        (set.borderColor =
          _mode.value === "dark"
            ? _darkOptions.value.darkBgColor
            : _darkOptions.value.darkBgColorLight)
    );
  }

  function changeMode(mode: "light" | "dark") {
    _mode.value = mode;

    if (_isAnimating.value) {
      return;
    }

    renderChart(true);
  }

  watch(
    () => _isAnimating.value,
    (isAnimating) => {
      if (isAnimating) {
        return;
      }
      setTimeout(() => {
        renderChart(true);
      }, 50); // slight delay to ensure animation has fully finished before re-rendering chart
    }
  );

  onUnmounted(() => {
    clearTimeout(_timeout.value);
  });

  return {
    setupCharts,
    renderChart,
    addDataLabels,
    destroyChart,
    updateChart,
    changeMode,
    updateDarkmodeOptions,
  };
}

export default generateCharts;
